import { baseCodeApi } from '@/shared/api/baseCodeApi';

export type CmBaseCodeOption = { title: string; value: string };

let b04OptionsCache: CmBaseCodeOption[] = [];
let b05OptionsCache: CmBaseCodeOption[] = [];
let b15OptionsCache: CmBaseCodeOption[] = [];
let b08OptionsCache: CmBaseCodeOption[] = [];

export function getB04Options(): CmBaseCodeOption[] {
  return b04OptionsCache;
}

export function getB05Options(): CmBaseCodeOption[] {
  return b05OptionsCache;
}

export function mapCmBaseCodeOption(code: unknown): CmBaseCodeOption {
  const raw = code as Record<string, unknown>;
  const value = String(raw.totalCode || raw.TOTAL_CODE || '').trim();
  const title = String(
    raw.codeName || raw.CODE_NAME || raw.defaultCodeName || raw.DEFAULT_CODE_NAME || '',
  ).trim();
  return { title, value };
}

/**
 * B15(관할/국가) 전용 매핑 — bizfile 은 국가를 ISO2 로 받으므로 value 를 BIGO1(예: "SG", "AL") 로,
 * 표시는 CODE_NAME("Singapore", "Albania") 으로 한다.
 */
export function mapB15Option(code: unknown): CmBaseCodeOption {
  const raw = code as Record<string, unknown>;
  const value = String(raw.bigo1 || raw.BIGO1 || '').trim().toUpperCase();
  const title = String(
    raw.codeName || raw.CODE_NAME || raw.defaultCodeName || raw.DEFAULT_CODE_NAME || '',
  ).trim();
  return { title, value };
}

/**
 * B08(통화) 전용 매핑 — bizfile 은 통화를 3글자 ISO("SGD","USD") 로 받으므로 value 를 BIGO3 로,
 * 표시는 "SGD — 이름" 형태로 한다.
 */
export function mapB08Option(code: unknown): CmBaseCodeOption {
  const raw = code as Record<string, unknown>;
  const value = String(raw.bigo3 || raw.BIGO3 || '').trim().toUpperCase();
  const name = String(
    raw.codeName || raw.CODE_NAME || raw.defaultCodeName || raw.DEFAULT_CODE_NAME || '',
  ).trim();
  const title = value && name ? `${value} — ${name}` : value || name;
  return { title, value };
}

export function isSelectableCmBaseCode(opt: CmBaseCodeOption): boolean {
  if (!opt.title || !opt.value) return false;
  if (opt.value.slice(-3) === '000') return false;
  const lower = opt.title.toLowerCase();
  if (lower.includes('please select') || lower === 'select') return false;
  return true;
}

export function getB15Options(): CmBaseCodeOption[] {
  return b15OptionsCache;
}

export function getB08Options(): CmBaseCodeOption[] {
  return b08OptionsCache;
}

/**
 * 진행 중인 요청 — 같은 코드에 대한 동시 호출을 하나로 합친다.
 *
 * 캐시는 **응답이 온 뒤에야** 채워지므로, 화면이 처음 뜰 때 국적/통화 입력칸이
 * 수십 개면 전부 "캐시 비었음"을 보고 각자 요청을 날린다(인코퍼레이션처럼 이사·주주·
 * UBO·컨트롤러 표가 많은 화면에서 수십 건). 같은 코드는 한 번만 부르게 한다.
 */
const inflight = new Map<string, Promise<CmBaseCodeOption[]>>();

export function loadCmBaseCodeOptions(
  mainCode: 'B04' | 'B05' | 'B15' | 'B08',
): Promise<CmBaseCodeOption[]> {
  const running = inflight.get(mainCode);
  if (running) return running;
  const p = fetchCmBaseCodeOptions(mainCode).finally(() => inflight.delete(mainCode));
  inflight.set(mainCode, p);
  return p;
}

async function fetchCmBaseCodeOptions(
  mainCode: 'B04' | 'B05' | 'B15' | 'B08',
): Promise<CmBaseCodeOption[]> {
  const res = await baseCodeApi.getCodesByMainCode('CM', mainCode, undefined);
  const codes = (res as { codes?: unknown[]; data?: { codes?: unknown[] } }).codes
    || (res as { data?: { codes?: unknown[] } }).data?.codes
    || [];
  const mapper =
    mainCode === 'B15' ? mapB15Option : mainCode === 'B08' ? mapB08Option : mapCmBaseCodeOption;
  const options = (codes as unknown[]).map(mapper).filter(isSelectableCmBaseCode);
  if (mainCode === 'B04') b04OptionsCache = options;
  if (mainCode === 'B05') b05OptionsCache = options;
  if (mainCode === 'B15') b15OptionsCache = options;
  if (mainCode === 'B08') b08OptionsCache = options;
  return options;
}

export function labelForCmBaseCode(
  codeOrText: string,
  options: CmBaseCodeOption[],
): string {
  const s = String(codeOrText || '').trim();
  if (!s) return '';
  const byCode = options.find((o) => o.value === s);
  if (byCode) return byCode.title;
  return s;
}

/** 표시명 또는 B04/B05 코드 → TOTAL_CODE */
export function resolveCmBaseCodeValue(
  textOrCode: string,
  options: CmBaseCodeOption[],
): string {
  const s = String(textOrCode || '').trim();
  if (!s) return '';
  if (options.some((o) => o.value === s)) return s;
  const lower = s.toLowerCase();
  const exact = options.find((o) => o.title.toLowerCase() === lower);
  if (exact) return exact.value;
  const partial = options.find(
    (o) => o.title.toLowerCase().includes(lower) || lower.includes(o.title.toLowerCase()),
  );
  return partial?.value || s;
}

export function nationalityLabelForOutput(codeOrText: string): string {
  return labelForCmBaseCode(codeOrText, b04OptionsCache);
}

export function mobileCountryLabelForOutput(codeOrText: string): string {
  return labelForCmBaseCode(codeOrText, b05OptionsCache);
}
